import { type JSX, type KeyboardEvent, useEffect, useRef, useState } from 'react';

import {
  liste as artikelliste,
  PRODUKTARTNAME,
  VERTRIEBSFORMNAME,
  type Artikelzeile,
  type Produktart,
} from '../api/produkte';
import { Schliesskreuz } from '../komponenten/Schliesskreuz';

/**
 * DIE ARTIKELAUSWAHL ALS FENSTER (ANG-02, AUF-03).
 *
 * Bis hierher klappte unter dem Suchfeld eine Liste auf. Das hat zwei Mängel,
 * die im Beleg mehr wiegen als anderswo:
 *
 *   DIE LISTE VERDRÄNGT DAS FORMULAR. Sie schiebt Menge, Preis und Steuersatz
 *   nach unten, und wer den achten Treffer sucht, verliert die Felder aus dem
 *   Blick, die er gleich braucht.
 *
 *   SIE ZEIGT ZU WENIG. Acht Zeilen mit Nummer und Bezeichnung — mehr passt
 *   nicht unter ein Feld. Die Vertriebsform, an der die Preisstellung hängt
 *   (PRD-42), war nicht zu sehen; man wählte, ohne zu wissen, was man wählt.
 *
 * Das Fenster ist dem allgemeinen Suchfeld nachgebaut und teilt seine Klassen:
 * Schleier, Dialog, Trefferliste, Tastaturführung. Wer eines kennt, kennt
 * beide — und eine zweite Gestaltung für dieselbe Handlung wäre eine, die
 * auseinanderläuft.
 *
 * Es ist NICHT das Suchfenster selbst: Das kennt Kunden, Angebote, Aufträge
 * und Termine, aber keine Artikel; es dort einzuhängen hieße, seine
 * Feldersuche, seinen Verlauf und seine Sprungziele um eine Art zu erweitern,
 * die keinen davon braucht.
 *
 * ============================================================================
 * ES ÖFFNET NICHT MEHR LEER (13.09.2026)
 * ============================================================================
 *
 * Auftraggeber: „das fenster für die artikelauswahl im Angebot und im Auftrag
 * soll standardmäßig die häufigsten artikel anzeigen, aber über eine auswahl
 * links auch die verschiedenen produktarten anzeigen und dann filtern lassen."
 *
 * Vorher stand dort „ab drei Zeichen" und sonst nichts. Wer den Katalog nicht
 * auswendig kennt, konnte nicht einmal nachsehen, was es gibt — ein Fenster,
 * das erst nach einer richtigen Eingabe etwas zeigt, setzt voraus, was es
 * beantworten sollte.
 *
 * DIE HÄUFIGKEIT KOMMT AUS DEM SERVER (`sortierung: 'haeufigkeit'`): wie oft
 * ein Artikel in den letzten zwölf Monaten auf einem Angebot oder Auftrag
 * stand. Sie hier zu rechnen hieße, jede Belegposition in den Browser zu
 * laden.
 *
 * DAS FILTERN NACH ART GESCHIEHT ÖRTLICH. Der wählbare Katalog dieses Hauses
 * hat rund vierzig Zeilen und ist beim Öffnen schon da; eine zweite Abfrage je
 * Klick wäre eine Wartezeit für eine Auskunft, die im Speicher liegt. Die
 * SUCHE bleibt im Server — sie verzeiht Tippfehler (KUN-22), und das kann
 * keine Liste im Browser.
 */

/** Ein Eintrag der linken Spalte: eine Art mit ihrer Zahl. */
type Artzeile = { readonly art: Produktart | ''; readonly name: string; readonly anzahl: number };

export function Artikelwahl({
  beiWahl,
  beiSchliessen,
}: {
  readonly beiWahl: (artikel: Artikelzeile) => void;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const [suche, setzeSuche] = useState('');
  /** Der wählbare Katalog, nach Häufigkeit geordnet — einmal beim Öffnen geholt. */
  const [bestand, setzeBestand] = useState<readonly Artikelzeile[]>([]);
  const [treffer, setzeTreffer] = useState<readonly Artikelzeile[]>([]);
  const [art, setzeArt] = useState<Produktart | ''>('');
  const [laedt, setzeLaedt] = useState(true);
  const [markiert, setzeMarkiert] = useState(0);
  const feld = useRef<HTMLInputElement>(null);

  /*
   * DER FOKUS KOMMT ZURÜCK (Auftrag CRM-102, wie im Suchfenster): Kreuz, Esc,
   * Schleier und Wahl geben ihn an den Knopf, der das Fenster geöffnet hat.
   * Ohne das Merken stünde er nach dem Schließen am Seitenanfang.
   */
  useEffect(() => {
    const herkunft = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    feld.current?.focus();
    return () => herkunft?.focus();
  }, []);

  /*
   * DER KATALOG BEIM ÖFFNEN — einmal, nach Häufigkeit.
   *
   * Ein Fehlschlag bleibt still: Dann steht die Liste leer da, und die Suche
   * geht trotzdem. Eine Fehlermeldung über einer Auswahlliste, die man auch
   * durchsuchen kann, wäre lauter als die Sache.
   */
  useEffect(() => {
    let verworfen = false;
    void artikelliste({ nurWaehlbar: true, sortierung: 'haeufigkeit' })
      .then((e) => {
        if (verworfen) return;
        setzeBestand(e.artikel);
        setzeLaedt(false);
      })
      .catch(() => {
        if (verworfen) return;
        setzeBestand([]);
        setzeLaedt(false);
      });
    return () => {
      verworfen = true;
    };
  }, []);

  /*
   * AB DREI ZEICHEN, wie unter dem Feld auch — die unscharfe Suche nach
   * KUN-22 liefert bei zwei Zeichen halbe Kataloge. Die Entprellung hält
   * die Tastatur flüssig; ohne sie liefe je Anschlag eine Abfrage.
   */
  useEffect(() => {
    if (suche.trim().length < 3) {
      setzeTreffer([]);
      setzeMarkiert(0);
      return;
    }
    let verworfen = false;
    setzeLaedt(true);
    const uhr = setTimeout(() => {
      void artikelliste({ suche: suche.trim(), nurWaehlbar: true })
        .then((e) => {
          if (verworfen) return;
          setzeTreffer(e.artikel.slice(0, 25));
          setzeMarkiert(0);
          setzeLaedt(false);
        })
        .catch(() => {
          if (verworfen) return;
          setzeTreffer([]);
          setzeLaedt(false);
        });
    }, 250);
    return () => {
      verworfen = true;
      clearTimeout(uhr);
    };
  }, [suche]);

  const sucht = suche.trim().length >= 3;

  /*
   * WAS IN DER LISTE STEHT: gesucht oder häufig, in beiden Fällen auf die
   * gewählte Art eingegrenzt.
   *
   * Die Grenze von 25 gilt für beides. Ein Fenster mit hundert Zeilen ist
   * keine Auswahl mehr, sondern ein zweiter Katalog — wer so viel sieht,
   * sucht (M-10 sinngemäß).
   */
  const gezeigt = (sucht ? treffer : bestand)
    .filter((a) => art === '' || a.produktart === art)
    .slice(0, 25);

  /*
   * DIE LINKE SPALTE ZEIGT NUR ARTEN, DIE ES GIBT.
   *
   * `PRODUKTARTNAME` führt sieben; im Beleg kommen davon selten mehr als drei
   * vor. Eine Art mit null Zeilen anzubieten heißt, einen Klick anzubieten,
   * der zu einer leeren Liste führt — und dann sucht jemand den Fehler.
   *
   * Gezählt wird am BESTAND und nicht an der Suche: Sonst wanderten die Zahlen
   * mit jedem Anschlag, und die Spalte, die Ordnung geben soll, wäre die
   * unruhigste Stelle des Fensters.
   */
  const arten: readonly Artzeile[] = [
    { art: '', name: 'Alle', anzahl: bestand.length },
    ...[...new Set(bestand.map((a) => a.produktart))]
      .map((k) => ({
        art: k,
        name: PRODUKTARTNAME[k],
        anzahl: bestand.filter((a) => a.produktart === k).length,
      }))
      .sort((x, y) => y.anzahl - x.anzahl || x.name.localeCompare(y.name)),
  ];

  /*
   * DIE TASTATUR FÜHRT ALLEIN (NFA-07). Der Fänger sitzt am Fenster und nicht
   * am Dokument: Ein globaler Fänger griffe auch, wenn das Fenster zu ist,
   * und fangte ↑ ↓ in der Maske dahinter ab.
   */
  function beiTaste(e: KeyboardEvent<HTMLDivElement>): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      beiSchliessen();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setzeMarkiert((i) => Math.min(i + 1, Math.max(0, gezeigt.length - 1)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setzeMarkiert((i) => Math.max(0, i - 1));
      return;
    }
    if (e.key === 'Enter') {
      const zeile = gezeigt[markiert];
      if (zeile !== undefined && zeile.hatPreis) {
        e.preventDefault();
        beiWahl(zeile);
      }
    }
  }

  const ohnePreis = gezeigt.some((t) => !t.hatPreis);

  return (
    <div className="suchfenster" onKeyDown={beiTaste}>
      <button
        type="button"
        className="suchfenster__schleier"
        aria-label="Artikelauswahl schließen"
        onClick={beiSchliessen}
      />

      <div
        className="suchfenster__fenster"
        role="dialog"
        aria-modal="true"
        aria-labelledby="artikelwahl-titel"
      >
        <div className="suchfenster__titel">
          <h2 id="artikelwahl-titel">Artikel auswählen</h2>
          <span className="suchfenster__hinweis">
            Nummer oder Bezeichnung — die Suche verzeiht Tippfehler
          </span>
          <span className="taste" aria-hidden="true">
            Esc
          </span>
          {/* Auftrag CRM-102: das Kreuz für Maus und Finger, Esc bleibt für die Tastatur. */}
          <Schliesskreuz beiKlick={beiSchliessen} />
        </div>

        <div className="suchfenster__haupt">
          <div className="suchfenster__felder">
            <label className="k" htmlFor="artikelwahl-suche">
              Suche
            </label>
            <div className="suchfenster__feldreihe">
              <input
                id="artikelwahl-suche"
                ref={feld}
                className="suchfenster__feld"
                type="text"
                value={suche}
                onChange={(e) => setzeSuche(e.target.value)}
                placeholder="Bezeichnung oder Artikelnummer"
                autoComplete="off"
              />
            </div>
          </div>

          <div className="artikelwahl__spalten">
            {/*
              DIE ARTEN LINKS (13.09.2026).

              Als Liste von Knöpfen und nicht als Auswahlfeld: Sie stehen
              nebeneinander sichtbar, man sieht die Zahlen, und ein Klick
              genügt. Ein `select` verstecke die Auswahl hinter einem
              Aufklappen — bei drei bis vier Einträgen ist das ein Schritt zu
              viel für eine Sache, die man beim Erfassen dauernd wechselt.
            */}
            <nav className="artikelwahl__arten" aria-label="Produktart">
              {arten.map((a) => (
                <button
                  key={a.art === '' ? 'alle' : a.art}
                  type="button"
                  className={`artikelwahl__art${art === a.art ? ' artikelwahl__art--hier' : ''}`}
                  aria-pressed={art === a.art}
                  onClick={() => {
                    setzeArt(a.art);
                    setzeMarkiert(0);
                  }}
                >
                  {a.name}
                  <span className="artikelwahl__zahl">{a.anzahl}</span>
                </button>
              ))}
            </nav>

            <div className="artikelwahl__liste">
              <p className="suchfenster__zaehlung">
                {laedt
                  ? 'wird geladen …'
                  : sucht
                    ? `${String(gezeigt.length)} Treffer`
                    : gezeigt.length === 0
                      ? 'kein wählbarer Artikel'
                      : /*
                          „HÄUFIG VERWENDET" STEHT NUR DA, WENN ES STIMMT.
                          Hat noch nie jemand einen Artikel auf einen Beleg
                          genommen, ist die Ordnung alphabetisch — und dann
                          wäre die Überschrift eine Behauptung.
                        */
                        bestand.some((a) => a.verwendungen > 0)
                        ? 'häufig verwendet'
                        : `${String(gezeigt.length)} Artikel`}
              </p>

              <ul className="suchfenster__liste" role="listbox" aria-label="Artikel">
                {gezeigt.map((t, stelle) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      className={`suchfenster__treffer${
                        stelle === markiert ? ' suchfenster__treffer--hier' : ''
                      }`}
                      role="option"
                      aria-selected={stelle === markiert}
                      /*
                       * GEMESSEN am 31.08.2026: Ein Artikel ohne gepflegten
                       * Preis ließ sich wählen, und erst der Klick auf
                       * „Hinzufügen" brachte die Meldung. Ein Weg, der
                       * zuverlässig scheitert, gehört nicht angeboten
                       * (PRD-11, ANG-05).
                       */
                      disabled={!t.hatPreis}
                      onMouseEnter={() => setzeMarkiert(stelle)}
                      onClick={() => beiWahl(t)}
                    >
                      <span className="suchfenster__wer">
                        <b>{t.bezeichnung}</b>
                        <small>
                          {t.artikelnummer}
                          {t.warengruppenname !== null && ` · ${t.warengruppenname}`}
                          {/*
                            PRD-42: Die Vertriebsform ist die Preisstellung.
                            Sie stand in der Liste unter dem Feld nicht, und
                            man wählte einen Artikel, ohne zu wissen, in
                            welcher Form er verkauft wird.
                          */}
                          {t.vertriebsformen.length > 0
                            && ` · ${t.vertriebsformen.map((f) => VERTRIEBSFORMNAME[f]).join(', ')}`}
                        </small>
                      </span>
                      {/*
                        DIE ZAHL SAGT, WARUM DIE ZEILE OBEN STEHT.

                        Ohne sie wäre die Reihenfolge eine Behauptung — und
                        wer sie nicht erklären kann, hält sie für zufällig.
                      */}
                      {t.verwendungen > 0 && (
                        <span className="artikelwahl__mal">{t.verwendungen}×</span>
                      )}
                      {!t.hatPreis && (
                        <span className="marke marke--gesperrt">kein Preis gepflegt</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {ohnePreis && (
            <p className="feld__hilfe">
              {/* ANG-05 */}
              Für einen Artikel ohne gepflegten Preis findet die Preisfindung nichts, und die
              Position wird
              abgewiesen. Pflegen Sie den Preis in der Artikelakte unter „Preise" — oder legen Sie
              eine Freitextposition mit eigenem Betrag an.
            </p>
          )}

          <div className="suchfenster__fuss">
            <span className="suchfenster__hinweis">
              <span className="taste">↑</span> <span className="taste">↓</span> wählen ·{' '}
              <span className="taste">⏎</span> übernehmen · <span className="taste">Esc</span>{' '}
              schließen
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
